import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Crumb } from "./AppShell";

/**
 * The breadcrumb trail, set by whichever page is rendered.
 *
 * A context rather than deriving the trail from the URL, because the useful
 * labels are data the page has already fetched — "Nymbl · Plan Audit ›
 * Plan v3 › run r-0042" cannot be reconstructed from
 * `/runs/r-0042`, and a trail of raw slugs and ids would be worse than none.
 *
 * Pages call `useCrumbs`. It replaces the trail rather than pushing onto it, so
 * a page never has to know what came before it.
 */

interface CrumbsContextValue {
  crumbs: Crumb[];
  setCrumbs: (crumbs: Crumb[]) => void;
}

const CrumbsContext = createContext<CrumbsContextValue>({ crumbs: [], setCrumbs: () => {} });

export function CrumbsProvider({ children }: { children: React.ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <CrumbsContext.Provider value={value}>{children}</CrumbsContext.Provider>;
}

export function useCrumbsValue(): Crumb[] {
  return useContext(CrumbsContext).crumbs;
}

/**
 * Publish this page's trail. Safe to call with a value that changes as data
 * arrives — the effect keys on the trail's CONTENT, not on array identity, so a
 * fresh array of the same crumbs each render does not loop.
 */
export function useCrumbs(crumbs: Crumb[]): void {
  const { setCrumbs } = useContext(CrumbsContext);
  const signature = JSON.stringify(crumbs);
  useEffect(() => {
    // Parsed back rather than closing over `crumbs`, so the effect's only
    // dependency is the signature and eslint-style exhaustive-deps reasoning
    // stays honest.
    setCrumbs(JSON.parse(signature) as Crumb[]);
  }, [signature, setCrumbs]);
}
